/**
 * Result bottom sheet — rendered the moment the wheel lands (winner is
 * already known from the `ferris_round_spinning` socket event), then patched
 * in-place: the podium from `ferris_round_finished`, and the player's own
 * result ONLY from the server-confirmed result (`ferris_my_result` or
 * GET /round/:id/me) via applyMyResultToSheet. Local stakes never decide a win.
 *
 * Class names are synchronised with styles.css:
 *   .sheet-backdrop, .bottom-sheet, .sheet-handle,
 *   .winning-slot-card (with .emoji, .name, .multiplier),
 *   .your-result.win/.lose/.none,
 *   .winners-podium (top-three ranked winners), with skeleton cards while pending,
 *   .fairness-drawer.
 */

let _activeSheet = null; // { round, slots, currentUserId, myBets, confirmed }

export function showResultSheet({ round, winners, slots, currentUserId, myBets }) {
  const root = document.getElementById('bottom-sheet-root');
  root.innerHTML = '';
  const winningSlot = (slots || []).find((s) => Number(s.id) === Number(round.winningSlotId));

  const backdrop = document.createElement('div');
  backdrop.className = 'sheet-backdrop';
  const sheet = document.createElement('div');
  sheet.className = 'bottom-sheet';

  const close = () => { root.innerHTML = ''; _activeSheet = null; };
  backdrop.onclick = close;

  // ----- Provably-fair drawer (visible immediately; server seed is revealed on spin start) -----
  const fairness = `
    <details class="fairness-drawer">
      <summary>🔒 Provably-fair details</summary>
      <code>Round #${round.roundNumber}</code>
      <code>Server hash: ${escapeHtml(round.serverSeedHash || '—')}</code>
      <code>Server seed: ${escapeHtml(round.serverSeed || '—')}</code>
      <code>Client seed: ${escapeHtml(round.clientSeed || '—')}</code>
      <code>Nonce: ${round.nonce ?? '—'}</code>
    </details>
  `;

  sheet.innerHTML = `
    <div class="sheet-handle"></div>
    <div class="sheet-round-tag">ROUND #${round.roundNumber}</div>
    <div class="winning-slot-card">
      <div class="winning-slot-eyebrow">♛ WINNING SLOT ♛</div>
      <div class="emoji">${winningSlot ? winningSlot.emoji : '🎁'}</div>
      <div class="name">${winningSlot ? escapeHtml(winningSlot.name) : 'Slot ' + round.winningSlotId}</div>
      <div class="multiplier">×${winningSlot ? winningSlot.multiplier : '?'}</div>
    </div>
    <div class="your-result-slot"></div>
    <div class="winners-header">
      <h3>Top Winners</h3>
      <span class="winners-count" data-pending="1">Tallying…</span>
    </div>
    <div class="winners-podium" data-pending="1" aria-label="Top 3 winners">${renderPodiumSkeleton()}</div>
    ${fairness}
    <button class="sheet-close-btn" type="button">Continue</button>
  `;

  // Wire close button
  const closeBtn = sheet.querySelector('.sheet-close-btn');
  if (closeBtn) closeBtn.onclick = close;

  root.appendChild(backdrop);
  root.appendChild(sheet);

  _activeSheet = { round, slots, currentUserId, myBets, confirmed: false };

  // The player's own result is unknown until the server confirms it. With
  // stakes on the board, show a neutral pending state — never "You won" from
  // local stakes (a stake the server never recorded would show a phantom win).
  const myTotalBet = Object.values(myBets || {}).reduce((a, b) => a + b, 0);
  const slotEl = sheet.querySelector('.your-result-slot');
  if (slotEl) {
    slotEl.innerHTML = myTotalBet > 0
      ? `<div class="your-result none pending">Confirming your result…</div>`
      : `<div class="your-result none">You didn't bet this round.</div>`;
  }

  // If winners are already known (e.g. recovery path), patch immediately.
  if (winners) updateResultSheet({ winners });
}

/**
 * Patch the active result sheet with the final winners list and the user's
 * realised payout. Safe no-op if no sheet is mounted.
 */
export function updateResultSheet({ winners, round } = {}) {
  if (!_activeSheet) return false;
  const sheet = document.querySelector('.bottom-sheet');
  if (!sheet) return false;

  const ctx = _activeSheet;
  if (round) ctx.round = round;
  const list = winners || [];
  sheet.classList.remove('winner-count-0', 'winner-count-1', 'winner-count-2', 'winner-count-3');

  // Count + ranked top-three winners podium
  const topWinners = buildTopWinners(list, ctx.currentUserId);
  sheet.classList.add(`winner-count-${Math.min(topWinners.length, 3)}`);
  const countEl = sheet.querySelector('.winners-count');
  if (countEl) {
    countEl.removeAttribute('data-pending');
    countEl.textContent = topWinners.length > 0
      ? `Top ${topWinners.length}`
      : 'No winners';
  }
  const podium = sheet.querySelector('.winners-podium');
  if (podium) {
    podium.removeAttribute('data-pending');
    podium.classList.remove('count-1', 'count-2', 'count-3');
    if (topWinners.length === 0) {
      podium.classList.add('is-empty');
      podium.innerHTML = `<div class="podium-empty">No winners this round — house keeps the pot.</div>`;
    } else {
      podium.classList.remove('is-empty');
      podium.classList.add(`count-${topWinners.length}`);
      podium.innerHTML = topWinners.map(renderPodiumWinner).join('');
    }
  }

  return true;
}

/**
 * Render the player's own result from the server-confirmed result
 * (GET /round/:id/me or the ferris_my_result push). Returns false when no
 * sheet for that round is open.
 */
export function applyMyResultToSheet(result) {
  if (!_activeSheet || !result) return false;
  const ctx = _activeSheet;
  const roundId = ctx.round && (ctx.round._id || ctx.round.roundId);
  if (roundId && result.roundId && roundId !== result.roundId) return false;
  const sheet = document.querySelector('.bottom-sheet');
  if (!sheet) return false;
  if (result.userId) ctx.currentUserId = result.userId;
  const firstConfirm = !ctx.confirmed;
  ctx.confirmed = true;

  const slotEl = sheet.querySelector('.your-result-slot');
  if (slotEl) slotEl.innerHTML = renderMyResult(result);
  if (firstConfirm && result.won) spawnConfetti();
  return true;
}

function renderMyResult(result) {
  if (result.status === 'voided') {
    return result.totalStake > 0
      ? `<div class="your-result none">Round cancelled — 💎 ${formatNum(result.refunded)} refunded.</div>`
      : `<div class="your-result none">Round cancelled.</div>`;
  }
  if (result.won) {
    const net = Number(result.net || 0);
    return `<div class="your-result win">🎉 You won 💎 ${formatNum(result.totalPayout)}${net > 0 ? ` <span class="net">(net +${formatNum(net)})</span>` : ''}!</div>`;
  }
  if (result.totalStake > 0) {
    return `<div class="your-result lose">Better luck next round! Lost 💎 ${formatNum(result.totalStake)}</div>`;
  }
  return `<div class="your-result none">You didn't bet this round.</div>`;
}

/**
 * Force-close any active bottom sheet (result or rules). Called when a new
 * round starts so the previous result panel doesn't stay open through the
 * next betting window.
 */
export function closeAnySheet() {
  const root = document.getElementById('bottom-sheet-root');
  if (root) root.innerHTML = '';
  _activeSheet = null;
}

function buildTopWinners(winners = [], currentUserId = null) {
  const byUser = new Map();
  for (const winner of winners) {
    if (!winner || Number(winner.payout || 0) <= 0) continue;
    const userId = String(winner.userId || winner._id || '').trim();
    if (!userId) continue;
    const current = byUser.get(userId) || {
      userId,
      userName: winner.userName || 'Player',
      userAvatar: winner.userAvatar || '',
      payout: 0,
      betAmount: 0,
      isMe: userId === currentUserId,
    };
    current.payout += Math.max(0, Math.floor(Number(winner.payout || 0)));
    current.betAmount += Math.max(0, Math.floor(Number(winner.amount || 0)));
    if (!current.userAvatar && winner.userAvatar) current.userAvatar = winner.userAvatar;
    if ((!current.userName || current.userName === 'Player') && winner.userName) current.userName = winner.userName;
    byUser.set(userId, current);
  }
  return Array.from(byUser.values())
    .sort((a, b) => (b.payout - a.payout) || (b.betAmount - a.betAmount) || String(a.userName).localeCompare(String(b.userName)))
    .slice(0, 3)
    .map((winner, index) => ({ ...winner, rank: index + 1 }));
}

function renderPodiumWinner(winner) {
  const initials = (winner.userName || 'P').trim().slice(0, 1).toUpperCase() || 'P';
  const avatarStyle = winner.userAvatar
    ? ` style="background-image:url('${escapeAttr(winner.userAvatar)}');background-size:cover;background-position:center;"`
    : '';
  return `
    <article class="podium-card rank-${winner.rank}${winner.isMe ? ' is-me' : ''}" aria-label="Rank ${winner.rank}: ${escapeAttr(winner.userName || 'Player')}">
      <div class="podium-rank">${winner.rank}</div>
      <div class="podium-avatar-frame" aria-hidden="true">
        <div class="podium-crown">♛</div>
        <div class="podium-avatar"${avatarStyle}>${winner.userAvatar ? '' : escapeHtml(initials)}</div>
      </div>
      <div class="podium-name">${escapeHtml(winner.userName || 'Player')}${winner.isMe ? ' <span class="you-tag">You</span>' : ''}</div>
      <div class="podium-payout">+💎 ${formatNum(winner.payout)}</div>
    </article>`;
}

function renderPodiumSkeleton() {
  return [2, 1, 3].map((rank) => `
    <article class="podium-card rank-${rank} skeleton-card" aria-hidden="true">
      <div class="podium-rank sk"></div>
      <div class="podium-avatar-frame"><div class="podium-avatar sk"></div></div>
      <div class="podium-name sk"></div>
      <div class="podium-payout sk"></div>
    </article>`).join('');
}

function spawnConfetti() {
  const colors = ['#ffdf7a', '#fff0b6', '#b56cff', '#ef4f7c', '#28c987', '#fff8de'];
  const layer = document.createElement('div');
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:95;overflow:hidden;';
  for (let i = 0; i < 28; i++) {
    const p = document.createElement('div');
    const c = colors[i % colors.length];
    const left = Math.random() * 100;
    const dur = 1.4 + Math.random() * 1.2;
    const delay = Math.random() * 0.4;
    const size = 6 + Math.random() * 6;
    p.style.cssText = `position:absolute;top:-20px;left:${left}vw;width:${size}px;height:${size * 1.6}px;background:${c};opacity:0.9;border-radius:2px;animation:confetti-fall ${dur}s ${delay}s ease-in forwards;transform:rotate(${Math.random() * 360}deg);`;
    layer.appendChild(p);
  }
  document.body.appendChild(layer);
  setTimeout(() => layer.remove(), 3500);
}

function formatNum(n) {
  if (n == null || isNaN(n)) return '—';
  return String(Math.floor(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function escapeHtml(s) { return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function escapeAttr(s) { return escapeHtml(s); }
